import { action, computed, thunk, thunkOn } from 'easy-peasy';
import * as R from 'ramda';
import { api } from '~/api';
import { history } from '~/history';
import { User } from '~/models';
import { toast } from 'react-toastify';
import * as tenderService from '~/services/tenders';
import { getSentryInstance } from '~/sentry';
import { defaultSetting } from './defaultSetting';

export const authModel = {
  user: null,
  solutionId: null,
  loading: true, // indicates token loading & sign-in, default to true to show loading spin before routing.
  routes: [],
  participantToast: null,

  // Selectors
  userModel: computed((state) => state.user && new User(state.user)),
  solutions: computed(R.pathOr([], ['user', 'solutions'])),
  solution: computed((state) =>
    R.find(R.propEq('id', state.solutionId), state.solutions)
  ),
  solutionColumns: computed((state) =>
    R.pathOr({}, ['columns'], state.solution)
  ),
  isdao: computed(
    (state) =>
      state.solution &&
      state.solution.id === 'c429cff0-a32a-40b2-9243-71547ae65a3a'
  ),

  solutionDivisions: computed((state) =>
    R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.map(R.path(['division']))
    )(state)
  ),

  solutionDivisionIds: computed((state) =>
    R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.map(R.path(['division', 'id']))
    )(state)
  ),

  solutionWarehouses: computed((state) =>
    R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.map(R.path(['division', 'warehouses'])),
      R.flatten
    )(state)
  ),

  canTradeDivisions: computed((state) =>
    R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.filter((role) =>
        R.includes(R.prop('role', role), ['manager', 'trader'])
      ),
      R.map((role) => R.prop('division', role))
    )(state)
  ),

  canManageDivisions: computed((state) => {
    return R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.filter((role) => R.includes(R.prop('role', role), ['manager'])),
      R.map((role) => R.prop('division', role))
    )(state);
  }),

  canManageProductsDivisions: computed((state) => {
    return R.pipe(
      R.pathOr([], ['solution', 'roles']),
      R.filter((role) => R.path(['division', 'manageproducts'], role)),
      R.map((role) => R.prop('division', role))
    )(state);
  }),

  userTradeActions: computed((state) => {
    let canBuy = false;
    let canSell = false;

    let actions = [];
    state.canTradeDivisions.forEach((item) => {
      if (
        !canBuy &&
        ['buyer', 'trader', 'broker'].includes(item.divisiontype)
      ) {
        canBuy = true;
        actions.push('buy');
      }
      if (
        !canSell &&
        ['seller', 'trader', 'broker'].includes(item.divisiontype)
      ) {
        canSell = true;
        actions.push('sell');
      }
    });

    return actions;
  }),

  hasRole: computed(
    (state) =>
      (role, solution = state.solution) =>
        R.includes(role, R.propOr([], 'roles', solution).map(R.prop('role')))
  ),

  hasRoleInAnySolution: computed(
    (state) =>
      (role, solutions = state.solutions) =>
        R.any(
          (solution) =>
            R.includes(
              role,
              R.propOr([], 'roles', solution).map(R.prop('role'))
            ),
          solutions
        )
  ),

  privacyCheckReq: computed((state) => {
    const { user, solution } = state;

    if (!user || !solution) return false;

    if (!solution.policies || solution.policies.length === 0) return false;

    return solution.policies.some((p) => p.accepted === false);
  }),

  isSolutionOwner: computed((state) =>
    R.pathEq(
      ['solution', 'owner', 'id'],
      R.path(['user', 'company', 'id'], state),
      state
    )
  ),

  canEditExchangeRates: computed(
    (state) =>
      R.pathOr(false, ['solution', 'settings', 'manualexchange'], state) &&
      state.isSolutionOwner &&
      state.hasRole('manager')
  ),

  traderType: computed((state) => {
    let [seller, buyer] = [false, false];
    for (const r of R.pathOr([], ['solution', 'roles'], state)) {
      if (R.path(['division', 'seller'], r)) seller = true;
      if (R.path(['division', 'buyer'], r)) buyer = true;
      if (seller && buyer) break;
    }
    return { seller, buyer };
  }),

  userPrefs: computed((state) => R.pathOr({}, ['user', 'prefs'], state)),

  solutionSettings: computed((state) => {
    const solution = R.find(R.propEq('id', state.solutionId), state.solutions);
    return {
      ...defaultSetting,
      ...solution?.settings,
      hasPartners: !!(solution?.columns?.buyer || solution?.columns?.consignee),
    };
  }),

  // Actions
  signIn: action((state, { user }) => {
    state.user = user;
  }),
  signOut: action((state) => {
    state.user = null;
  }),
  setLoading: action((state, loading) => {
    state.loading = loading;
  }),
  changeSolution: action((state, id) => {
    state.loading = true;
    state.solutionId = id;
  }),
  updateRoutes: action((state, routes) => {
    state.routes = R.sortBy(
      R.prop('key'),
      routes.map((item) => ({
        key: `${item.source.id}-${item.destination.id}`,
        ...item,
      }))
    );
  }),
  updateUser: action((state, data) => {
    state.user = { ...state.user, ...data };
  }),
  setParticipantToast: action((state, data) => {
    state.participantToast = data;
  }),
  dismissParticipants: action((state) => {
    const s = R.find(R.propEq('id', state.solution.id), state.solutions);
    state.user.solutions = [
      ...R.reject(R.propEq('id', state.solution.id), state.solutions),
      { ...s, new_participants: 0 },
    ];
  }),
  updateFavouriteproducts: action((state, favouriteproducts) => {
    const s = R.find(R.propEq('id', state.solution.id), state.solutions);
    state.user.solutions = [
      ...R.reject(R.propEq('id', state.solution.id), state.solutions),
      { ...s, favouriteproducts },
    ];
  }),

  // Thunks
  loadUserProfile: thunk(async (actions) => {
    const result = await api.getData({
      type: 'user',
      id: `profile`,
      getResult: (x) => x,
    });
    actions.updateUser(result);
  }),

  loadUserSolutions: thunk(async (actions) => {
    const result = await api.getData({
      type: 'user',
    });
    actions.updateUser({
      solutions: result.solutions,
    });
  }),

  startAuth: thunk(async (actions, _, { injections: { authService } }) => {
    setTimeout(() => authService.redirectToAuthorizeUrl(), 0);
  }),
  // Start token exchange cycle by using provided code
  checkAuth: thunk(
    async (actions, payload, { injections: { api, authService, history } }) => {
      let user;
      try {
        // from signin
        const target = R.pathOr({}, ['target'], payload);
        const camefrom = R.pathOr('/', ['camefrom'], payload);
        const redirect = R.pathOr('/', ['redirect'], payload);
        let token = R.pathOr(null, ['token'], payload);
        const urlSolution = R.pathOr('', ['urlSolution'], payload);

        if (!token) {
          authService.clearToken();
          token = await api.refresh();
        }

        api.updateToken(token);
        authService.saveToken(token);

        let sid = target.solution || urlSolution || authService.loadSolution();

        // check for solution override
        const UUID =
          '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

        const tradeRegex = new RegExp(`^/trades/(${UUID})$`);
        const previewTradeRegex = new RegExp(`^/preview/trade/(${UUID})$`);
        const tenderRegex = new RegExp(
          `^/tenders/(${UUID})(?:/orders/[0-9a-f-]+/details)?$`
        );

        const location = window.location;
        const pathName = location.pathname;

        const urlParams = new URLSearchParams(window.location.search);
        const orderParamId = urlParams.get('order');

        if (orderParamId) {
          try {
            const data = await api.getData({
              type: 'orders',
              id: orderParamId,
            });
            sid = data.solution.id;

            // we need to replace the pid in the url just in case pid is different from the db
            history.replace(
              `/?${new URLSearchParams({
                ...Object.fromEntries(urlParams.entries()),
                order: data.pid,
              })}`
            );
          } catch {
            // redirect to home
            history.replace('/');
          }
        } else if (tradeRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'trades',
              id: pathName.match(tradeRegex)[1],
            });
            sid = data.solution.id;
          } catch (e) {
            if (e?.response?.status !== 403) throw e;
          }
        } else if (previewTradeRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'trades',
              id: pathName.match(previewTradeRegex)[1],
            });
            sid = data.solution.id;
          } catch (e) {
            if (e?.response?.status !== 403) throw e;
          }
        } else if (tenderRegex.test(pathName)) {
          try {
            const data = await api.getData({
              type: 'tenders',
              id: pathName.match(tenderRegex)[1],
            });
            sid = data.solution.id;
          } catch (e) {
            if (e?.response?.status !== 403) throw e;
          }
        }

        if (!sid) {
          // fall back to first solution
          const res = await api.getDataV2Api(`/user`);
          sid = res?.user?.solutions[0].id;
        }

        user = await api.getData({ type: 'user' }, { solution: sid });

        // if we are getting user with empty solutions, we need to get user again
        if (user.solutions.length < 1) {
          const res = await api.getDataV2Api(`/user`);
          sid = res?.user?.solutions[0].id;
          user = await api.getData({ type: 'user' }, { solution: sid });
        }

        actions.signIn({ user, target, camefrom, urlSolution, redirect });
      } catch (e) {
        // only sign user out if 401 unauthorized
        if (e.response?.status === 401) {
          actions.setLoading(false);
          if (window.location.pathname !== '/signin') {
            actions.signOut({ preserveUrl: true });
          }
          return;
        }
        if (user?.solutions.length < 1) {
          throw e;
        }
        // To has an error display in render, we need to set up another variable for it in global store
        // message.error('Sign in failed. Please try again');
      }
      actions.setLoading(false);
    }
  ),

  fetchRoutes: thunk(async (actions, solutionId, { injections: { api } }) => {
    try {
      const params = { solution: solutionId };
      const routes = await api.getData2('/routes', params);
      actions.updateRoutes(routes.routes);
    } catch (e) {
      // nothing
    }
  }),

  fetchUser: thunk(async (actions, _, { injections: { api } }) => {
    try {
      const user = await api.getData({ type: 'user' });
      actions.updateUser(user);
    } catch (e) {
      // nothing
    }
  }),

  onSignIn: thunkOn(
    (actions) => actions.signIn,
    async (
      actions,
      { payload: { user, target, camefrom, urlSolution, redirect } },
      { injections: { authService }, getStoreActions }
    ) => {
      getStoreActions().auth.loadUserSolutions();
      const solutions = R.propOr([], 'solutions', user);
      /**
       * Set solution to be either:
       * 1. passed-in one through /authenticate
       * 2. ?solution= param in URL
       * 3. persistent one in localStorage
       * 4. first one of user.solutions
       */
      const sid = R.find(
        (id) => R.find(R.propEq('id', id), solutions),
        [
          target.solution,
          urlSolution,
          authService.loadSolution(),
          R.path([0, 'id'], solutions),
        ]
      );
      actions.changeSolution(sid);
      /**
       * TODO test scenarios for input URLs with user signed in or not.
       * - when prev token works, redirect to origin URL thru routing
       * - Otherwise, ideally we want to reach the origin URL
       */

      if (target.type) {
        try {
          switch (target.type) {
            case 'order':
              history.replace(`/?order=${target.id}`);
              return;

            case 'trade':
              history.replace(`/trades/${target.id}`);
              return;

            case 'participants':
              history.replace(
                `/participants?activatedon=gte:${target.activatedon}`
              );
              return;

            default:
              break;
          }
        } catch (error) {
          // nothing to worry about
        }
      }
      if (redirect !== false) {
        const urlParams = new URLSearchParams(window.location.search);
        const stayOnRoot =
          urlParams.has('solution') ||
          urlParams.has('order') ||
          target.solution;
        // Prioritize camefrom when it's a specific path (not root)
        if (camefrom && camefrom !== '/') {
          history.replace(camefrom);
        } else if (stayOnRoot) {
          history.replace('/');
        } else if (user.landingpage_access !== false) {
          history.replace('/home');
        } else {
          history.replace('/');
        }
      }
    }
  ),
  onSignOut: thunkOn(
    (actions) => actions.signOut,

    async (actions, { payload }, { injections: { api, authService } }) => {
      actions.setLoading(true);
      // Clear localStorage token immediately to prevent race condition if user navigates quickly
      // Keep api.token for the signOut call to authenticate
      authService.clearToken();
      tenderService.events.signout({});
      actions.changeSolution(null);
      // Destroy the cookie/session on server
      try {
        await api.signOut();
      } catch (error) {
        // Nothing
      }
      api.updateToken(null);

      // Directly redirect to root
      // TODO Ensure it works for different scenarios
      if (payload?.preserveUrl) {
        history.replace('/signin#signout', {
          from: window.location.pathname + window.location.search,
        });
      } else {
        history.replace('/signin#signout');
      }
      actions.setLoading(false);
    }
  ),
  onChangeSolution: thunkOn(
    (actions) => actions.changeSolution,
    async (
      actions,
      { payload: id },
      { injections: { authService }, getState, getStoreActions }
    ) => {
      const user = getState().user;

      getStoreActions().marketplace.reset();
      toast.dismiss(getState().participantToast);
      if (id) {
        authService.saveSolution(id);
        actions.setLoading(true);
        setTimeout(() => actions.setLoading(false), 1000);
        tenderService.events.solution({ solution: id });

        const sentry = getSentryInstance();
        if (sentry) {
          sentry.setUser({
            id: user.id,
            email: user.email,
            solution: id,
            company: user.company?.name,
          });
        }
      } else {
        authService.clearSolution(id);
      }
    }
  ),
};
